import type { Metadata } from "next";
import { formatAmount } from "@/lib/money";

/**
 * Canonical site origin used for metadata, canonical URLs, Open Graph and
 * JSON-LD. Set NEXT_PUBLIC_SITE_URL to your real production domain — the
 * fallback is a placeholder and MUST be overridden, or canonical/OG URLs will
 * point at the wrong host (which hurts SEO).
 */
export const SITE_URL = (process.env.NEXT_PUBLIC_SITE_URL ?? "https://fastapp.dz").replace(/\/$/, "");

export const SITE_NAME = "Fast";

/** Currency used across the catalogue (Algeria). */
/** Code ISO 4217, pour le balisage structuré uniquement. */
export const CURRENCY = "DZD";

/** Ce que lisent les gens. Le site affiche « 400 DA » partout ; « DZD » dans
 *  une phrase française détonne et n'est lu que par des machines. */
export const CURRENCY_LABEL = "DA";

/**
 * Minimum number of stores for a ZONE landing page (`/livraison/[ville]`) to be
 * indexable. Below this we keep the page (crawlable, linked, usable) but mark it
 * `noindex` — avoids thin/doorway pages diluting the site's authority.
 */
export const MIN_STORES_TO_INDEX = 6;

/**
 * Minimum number of stores for a ZONE × TERM combo page to be indexable.
 * Lower than the zone threshold: a combo is a narrower promise, so fewer stores
 * still make a genuinely useful page.
 */
export const MIN_STORES_TO_INDEX_COMBO = 5;

/**
 * A combo below `MIN_STORES_TO_INDEX_COMBO` is STILL indexed when it holds
 * essentially the whole local offer for that term — a page listing every sushi
 * restaurant in Algiers is exhaustive, not thin. Expressed as a share of the
 * parent zone's stores for the same term.
 */
export const EXHAUSTIVE_COVERAGE_RATIO = 0.8;

/**
 * Minimum priced menu items across a combo's restaurants for it to be indexable.
 * Store count alone is not depth: five restaurants sharing a dozen items make a
 * page with nothing on it. Only applied when the menu digest cache is warm —
 * missing data must never deindex a page.
 */
export const MIN_PRODUCTS_TO_INDEX_COMBO = 25;

/** Minimum distinct terms for a zone page to be substantive enough to index. */
export const MIN_TERMS_TO_INDEX = 3;

/** Build an absolute URL from a path, against SITE_URL. */
export function absoluteUrl(path: string): string {
  return `${SITE_URL}${path.startsWith("/") ? "" : "/"}${path}`;
}

/**
 * Metadata for surfaces that must never be indexed (account, cart, checkout,
 * auth…). Applied at the layout level so every page in the segment inherits it
 * — `robots.txt` alone does NOT prevent indexing of a URL that is linked to.
 */
export const PRIVATE_ROBOTS: NonNullable<Metadata["robots"]> = {
  index: false,
  follow: false,
  googleBot: { index: false, follow: false },
};

/**
 * Metadata for a private page: a title (so the tab is not "Fast — …") plus a
 * hard `noindex`. No canonical: a non-indexable page does not need one.
 */
export function privateMetadata(title: string): Metadata {
  return { title, robots: PRIVATE_ROBOTS };
}

/**
 * Metadata for a public, indexable page.
 *
 * `title` must NOT contain the brand — the root layout's title template appends
 * " · Fast" exactly once. Passing "… | Fast" here produced the duplicated
 * "| Fast · Fast" suffix seen in production.
 */
export function publicMetadata(opts: {
  title: string;
  description: string;
  path: string;
  images?: { url: string; alt?: string }[];
  index?: boolean;
}): Metadata {
  const { title, description, path, images, index = true } = opts;
  return {
    title,
    description,
    alternates: { canonical: path },
    robots: index ? undefined : { index: false, follow: true },
    openGraph: {
      type: "website",
      title,
      description,
      url: absoluteUrl(path),
      ...(images?.length ? { images } : {}),
    },
    twitter: {
      card: "summary_large_image",
      title,
      description,
      ...(images?.length ? { images: images.map((i) => i.url) } : {}),
    },
  };
}

/**
 * URL-safe slug from a free-text name: lowercase, accents stripped, emoji and
 * punctuation dropped, runs collapsed. Never emits "--", which is what lets it
 * be joined to a ULID with that separator and split back apart unambiguously.
 */
export function slugify(value: string): string {
  return value
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/['’]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 60)
    .replace(/-$/, "");
}

/**
 * Canonical path segment for a store: `kenz-sushi--01KV34N39K…`.
 *
 * The ULID stays in the URL and remains the lookup key — the slug is additive,
 * so a renamed store never 404s and no redirect map has to be maintained. A
 * bare ULID still resolves; the page redirects it to this form.
 *
 * Names that carry no Latin characters fall back to the bare ULID rather than
 * to a transliteration. 12 stores in the catalogue are named only in Arabic
 * ("مطعم بيت الضياف"); a machine transliteration would produce a URL that is
 * neither readable nor searchable, and percent-encoded Arabic is worse still
 * for a URL whose whole point is to survive being quoted.
 *
 * Readable URLs matter twice over here: they are a modest ranking and CTR
 * signal on Google, and they are what an answer engine reproduces when it
 * cites a source. "fastapp.dz/stores/kenz-sushi--01KV…" survives being quoted
 * in a chat answer; an opaque ULID does not.
 */
export function storeSegment(name: string, ulid: string): string {
  const slug = slugify(name);
  return slug ? `${slug}--${ulid}` : ulid;
}

/** Canonical path for a store page. */
export function storePath(name: string, ulid: string): string {
  return `/stores/${storeSegment(name, ulid)}`;
}

/**
 * The ULID inside a store path segment, in either form. Returns null when the
 * segment carries nothing that looks like a ULID.
 */
export function ulidFromSegment(segment: string): string | null {
  const decoded = decodeURIComponent(segment);
  const idx = decoded.lastIndexOf("--");
  const candidate = idx === -1 ? decoded : decoded.slice(idx + 2);
  return /^[0-9A-HJKMNP-TV-Z]{26}$/i.test(candidate) ? candidate.toUpperCase() : null;
}

/**
 * Google truncates a title around 60 characters. The root layout appends
 * " · Fast" to every page title, so a page's own text has ~53 to work with —
 * and a crawl of the built site found titles running to 89 characters, where
 * the store name itself was being cut off in the SERP.
 *
 * Give the descriptive suffixes longest-first; the first one that fits wins,
 * and the bare base is the last resort. The base is never truncated: losing
 * "L'Atelier du Pizzaiolo - Val d'Hydra" costs far more than losing
 * "— Livraison à domicile".
 */
/**
 * Retire les emoji d'un nom d'enseigne.
 *
 * 27 magasins sur 513 en portent un (« Tasty Crousty 🍚 - Draria »). Dans un
 * titre au budget de 53 caractères, ils coûtent de la place utile — et Google
 * les retire de toute façon de ses résultats.
 */
export function stripEmoji(name: string): string {
  return name
    .replace(/[\u{1F300}-\u{1FAFF}\u{2600}-\u{27BF}\u{FE0F}\u{200D}]/gu, "")
    .replace(/\s{2,}/g, " ")
    .trim();
}

/**
 * En dessous de ce nombre d'avis, la note n'est pas un argument : elle se lit
 * comme une note de complaisance plutôt que comme une preuve. 294 magasins sur
 * 513 passent ce seuil, dont toutes les fiches à fort trafic.
 */
export const MIN_REVIEWS_FOR_TITLE = 10;

/** « 4,7/5 (591 avis) », « 4,5/5 (38 351 avis) » — décimale et séparateur de
 *  milliers français, comme le reste du site. */
export function ratingLabel(average: number, total: number): string {
  return `${average.toFixed(1).replace(".", ",")}/5 (${formatAmount(total)} avis)`;
}

/** Première lettre en capitale, pour une clause qui suit un point. */
export function sentenceCase(text: string): string {
  return text ? text[0].toUpperCase() + text.slice(1) : text;
}

/** Au-delà, Google tronque la description dans ses résultats. */
export const DESCRIPTION_BUDGET = 158;

export const TITLE_BUDGET = 60 - " · Fast".length;

export function composeTitle(base: string, suffixes: string[]): string {
  for (const suffix of suffixes) {
    const candidate = suffix ? `${base} — ${suffix}` : base;
    if (candidate.length <= TITLE_BUDGET) return candidate;
  }
  return base;
}
